import { HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { Skeleton } from '@mui/material';
import { PhoneIcon } from '../icons';

export const AVATAR_VARIANT = {
  rounder: 'rounder',
  rounderSmall: 'rounder-small',
  rounderLarge: 'rounder-large',
  huge: 'huge',
  'x-large': 'x-large',
  experience: 'experience',
  large: 'large',
  normal: 'normal',
  small: 'small',
  'x-small': 'x-small',
} as const;
export type AvatarVariant = keyof typeof AVATAR_VARIANT;

export const variantClasses: Record<AvatarVariant, string> = {
  rounder: 'h-[90px] w-[90px] rounded-[100%_100%_100%_50%]',
  rounderSmall: 'h-[51px] w-[51px] rounded-[100%_100%_100%_50%]',
  rounderLarge: 'h-[150px] w-[150px] rounded-[100%_100%_100%_12%]',
  huge: 'h-[200px] w-[200px] rounded-[100%_100%_100%_12%]',
  'x-large': 'h-40 w-40 rounded-[100%_100%_100%_12%]',
  experience: 'h-28 w-28 rounded-[100%_100%_100%_12%]',
  large: 'h-[100px] w-[100px] rounded-[100%_100%_100%_12%]',
  normal: 'h-16 w-16 rounded-[100%_100%_100%_12%]',
  small: 'h-10 w-10 rounded-[100%_100%_100%_12%]',
  'x-small': 'h-7 w-7 rounded-[100%_100%_100%_12%]',
};

const availableVariants: Record<AvatarVariant, string> = {
  rounder: '',
  rounderSmall: '',
  rounderLarge: '',
  huge: 'bottom-[13px] left-[13px] w-[15px] h-[15px] border-2',
  'x-large': 'top-[137px] left-[7px] w-7 h-7 p-1 border-2',
  experience: 'top-[75px] left-2 w-7 h-7 p-1 border-2',
  large: 'top-[80px] left-2 w-3.5 h-3.5 border',
  normal: 'top-[50px] left-1 w-[10px] h-[10px] border',
  small: 'top-[30px] left-[.2rem] w-2 h-2 border',
  'x-small': 'top-[20px] left-[.15rem] w-1.5 h-1.5 border',
};

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  variant: AvatarVariant;
  image: string;
  imgClassName?: string;
  available?: boolean;
  isPeerListener?: boolean;
}

const Avatar = ({ variant, image, available, isPeerListener, className, imgClassName, ...rest }: AvatarProps) => {
  return (
    <div {...rest} className={twMerge('relative min-w-max', className)}>
      {/* removed non phone icon avatars */}
      {available && isPeerListener && variant !== 'experience' && variant !== 'x-large' && (
        <div className={`absolute z-10 rounded-full border-white bg-[#00DA6B] ${availableVariants[variant]}`} />
      )}
      {available && isPeerListener && (variant === 'experience' || variant === 'x-large') && (
        <PhoneIcon
          color="#FFFFFF"
          className={`absolute z-10 rounded-full bg-green-500 ${availableVariants[variant]}`}
        />
      )}
      {!available && isPeerListener && variant !== 'experience' && variant !== 'x-large' && (
        <div className={`absolute z-10 rounded-full border-white bg-slate-400 ${availableVariants[variant]}`} />
      )}
      {!available && isPeerListener && (variant === 'experience' || variant === 'x-large') && (
        <PhoneIcon color="#FFFFFF" className={`absolute z-10 rounded-full bg-sky-950 ${availableVariants[variant]}`} />
      )}

      <img alt="" className={twMerge('flex items-center', variantClasses[variant], imgClassName)} src={image} />
    </div>
  );
};

export default Avatar;

export interface AvatarSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant: AvatarVariant;
  skeletonClassName?: string;
  isPeerListener?: boolean;
}

export const AvatarSkeleton = ({
  variant,
  isPeerListener,
  className,
  skeletonClassName,
  ...rest
}: AvatarSkeletonProps) => (
  <div {...rest} className={twMerge('relative min-w-max', className)}>
    {isPeerListener && (
      <span
        className={twMerge('absolute z-10 overflow-hidden !rounded-full border-white', availableVariants[variant])}
      />
    )}
    <span className={twMerge('flex overflow-hidden', variantClasses[variant], skeletonClassName)}>
      <Skeleton
        sx={{
          width: 'inherit',
          height: 'inherit',
        }}
        className="!scale-100"
      />
    </span>
  </div>
);
